/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Components
import BaseControl from 'shared/ui/components/form-components/BaseControl.vue';

// Utils
import PickedFile from 'shared/ui/components/form-components/pickers/picked-file';

/**
 * This component is used to pick files.
 *
 * It has all the properties of the BaseControl plus:
 *
 * @vue-prop {String[]} extensions a list of allowed files extenstions to pick
 * @vue-prop {Boolean} multiple if true multiple file picking will be possible
 * @vue-prop {Number} size the maximum file size in bytes
 * @vue-prop {Number} maximum the maximum number of files
 * @vue-prop {String} [forceName] the output file name (if multiple is set then they will be suffixed with incremental value)
 *
 * @category ui.components.form-elements.pickers
 * @exports FilePicker
 */
const __vue_script__ = {
    name: 'FilePicker',

    extends: BaseControl,

    props: {
        value: {
            type: Array,
            default: null
        },

        extensions: {
            type: Array,
            default: null
        },

        multiple: {
            type: Boolean,
            default: false
        },

        alwaysReplace: {
            type: Boolean,
            default: false
        },

        size: {
            type: Number,
            default: 0
        },

        maximum: {
            type: Number,
            default: 0
        },

        forceName: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            accept: this.extensions ? this.extensions.map(e => `.${e}`).join(',') : null,
            cssClass: '',
            maxFiles: this.multiple ? this.maximum : 1,
            validExtRegex: this.extensions ? new RegExp(`\.(${this.extensions.join('|')})$`, 'i') : null
        };
    },

    computed: {
        /**
         * @returns {Boolean} whether to show the dropzone or not
         */
        showDropzone() {
            return this.filesCount < this.maxFiles;
        },

        /**
         * @returns {Number} the current number of files
         */
        filesCount() {
            return (this.innerValue || []).length || 0;
        },

        innerValue: {
            /** @returns {PickedFile[]} */
            get() {
                // @ts-ignore
                return this.value || [];
            },

            /** @param {PickedFile[]} files */
            set(files) {
                this.$emit('input', files);
            }
        }
    },

    methods: {
        /**
         * Handler for the input's change event.
         *
         * @param {PickedFile[]|FileList} files new uploading file
         */
        changed(files) {
            // Initialize and validate
            files = Array.from(files).map(f => new PickedFile(f));

            if (!files.every(f => this.validate(f))) {
                return; // Bails at first invalid file
            }

            // Merge new values instead of overwrite the set
            let mergedList = this.merge(files);

            // Maximum items capping
            if (this.maxFiles) {
                mergedList = mergedList.slice(0, this.maxFiles);
            }

            // Always reset the input value
            // @ts-ignore
            this.$refs.input.value = '';

            this.innerValue = mergedList;
        },

        /**
         * Validates the given file
         *
         * @param {PickedFile} pickedFile the file to validate
         *
         * @returns {Boolean} true if the file is valid false otherwise
         */
        validate(pickedFile) {
            if (pickedFile.validated) {
                return true;
            }

            let message;

            // Some formal validations
            if (this.extensions) {
                if (!this.validExtRegex.test(pickedFile.file.name)) {
                    message = this.$i18n.t('file_upload.invalid_file', [this.extensions.join(', ')]).toString();
                }
            }

            if (this.size && pickedFile.file.size > this.size) {
                message = this.$i18n.t('file_upload.exceeded_size', [this.$utils.numbers.humanFileSize(this.size)]).toString();
            }

            if (message) {
                this.$modal.show('dialog', {
                    title: this.$t('general.warning'),
                    text: message,
                    buttons: [{
                        title: this.$t('general.close'),
                        closeOnClick: true,
                        class: 'btn'
                    }]
                });
            } else {
                pickedFile.validated = true;
            }

            return !message;
        },

        /**
         * Merges new and old value
         *
         * @param {PickedFile[]} files the file list to merge to the current value
         *
         * @returns {PickedFile[]} the merged file list
         *
         * @private
         */
        merge(files) {
            if (this.alwaysReplace) {
                return files;
            }

            // First replace old files
            const mergedFiles = this.innerValue.map(oFile => {
                const nFileIdx = files.findIndex(f => f.id === oFile.id);

                if (nFileIdx === -1) {
                    return oFile;
                }

                const nFile = files[nFileIdx];

                files.splice(nFileIdx, 1);

                // Newly uploaded copy of the old file?
                nFile.fresh = nFile.hasOwnProperty('fresh') ? nFile.fresh : true;

                return nFile;
            });

            // Concatenate all remaining new files
            return mergedFiles.concat(files);
        },

        /**
         * Replaces a file of the collection.
         * If the file does not exist it then gets appended.
         *
         * @param {PickedFile} file the file to replace
         */
        replace(file) {
            file.fresh = false;
            this.changed([file]);
        },

        /**
         * Removes a file from the collection.
         *
         * @param {PickedFile} file the file to remove
         * @param {PickedFile[]} [files] optional file list on which perform the operation
         * @param {Boolean} [emit=true] if the component should emit removed event or not
         *
         * @returns {PickedFile[]} the new file list
         */
        remove(file, files, emit = true) {
            const newSet = (files || this.innerValue).filter(f => f.id !== file.id);

            if (!files) this.innerValue = newSet;
            if (emit) this.$emit('removed', file);

            return newSet;
        },

        selectFiles() {
            // @ts-ignore
            this.$refs.input.click();
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.showDropzone),expression:"showDropzone"}],staticClass:"file-picker",class:[_vm.disabled ? 'disabled' : '', _vm.cssClass],on:{"dragenter":function($event){$event.preventDefault();},"dragover":function($event){$event.preventDefault();_vm.cssClass = 'active'},"dragleave":function($event){_vm.cssClass = ''}}},[_c('div',{staticClass:"dropbox",on:{"drop":function($event){$event.preventDefault();return _vm.changed($event.dataTransfer.files)},"dragenter":function($event){$event.preventDefault();}}},[_c('input',{ref:"input",staticClass:"input-file",attrs:{"type":"file","name":_vm.name,"multiple":_vm.multiple,"disabled":_vm.disabled,"accept":_vm.accept},on:{"change":function($event){return _vm.changed($event.target.files)}}}),_vm._v(_vm._s(_vm.$t('file_upload.drag_drop'))),_c('a',{staticClass:"has-action",on:{"click":_vm.selectFiles}},[_vm._v(" "+_vm._s(_vm.$t('file_upload.upload')))])])])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "FilePicker.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__